import { generateId } from './contentUtils';
import { normalizeBackgroundMapForPersistence, normalizeBackgroundPersistenceValue } from './presetTemplates';

export const DEFAULT_DIVIDER_CONTENT = {
  style: 'solid',
  thickness: 2,
  color: '#e5e7eb',
  spacing: 'medium',
  width: 'full',
  isSlideBreak: false
};

export const safeString = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  return typeof value === 'string' ? value : String(value);
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const normalizeTitleCardBackgroundSettings = (settings) => {
  if (!settings || typeof settings !== 'object') return settings;
  const next = { ...settings };
  const normalizedBackgroundImage = normalizeBackgroundPersistenceValue(next.backgroundImage || '');
  next.backgroundImage = normalizedBackgroundImage;
  if (typeof next.image === 'string' && /^blob:/i.test(next.image)) {
    next.image = '';
  }
  return next;
};

export const normalizeDividerContent = (incomingContent, issues = []) => {
  const recordIssue = (msg) => issues && issues.push(msg);

  // Attempt to recover from stringified objects or JSON strings
  let content = incomingContent;
  if (typeof incomingContent === 'string') {
    const trimmed = incomingContent.trim();
    if (trimmed === '[object Object]') {
      recordIssue('Divider content was coerced to string; defaulted.');
      content = null;
    } else if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && (trimmed.endsWith('}') || trimmed.endsWith(']'))) {
      try {
        content = JSON.parse(trimmed);
      } catch (error) {
        recordIssue('Divider content string failed to parse; defaulted.');
        content = null;
      }
    } else {
      recordIssue('Divider content provided as string; defaulted.');
      content = null;
    }
  }

  if (!isPlainObject(content)) {
    return { ...DEFAULT_DIVIDER_CONTENT };
  }

  const normalized = {
    ...DEFAULT_DIVIDER_CONTENT,
    ...content,
    thickness: Number.isFinite(Number(content.thickness)) ? Number(content.thickness) : DEFAULT_DIVIDER_CONTENT.thickness,
    isSlideBreak: content.isSlideBreak === true
  };

  return normalized;
};

const deepClone = (value) => JSON.parse(JSON.stringify(value ?? {}));

const sanitizeText = (value) => {
  const str = safeString(value, '');
  if (!str) return '';
  if (str.includes('[object Object]')) {
    const cleaned = str.replace(/\[object Object\]/g, '').replace(/\s+/g, ' ').trim();
    return cleaned;
  }
  return str;
};

const normalizeCardBackgroundSettingsForExport = (settings) => {
  if (!settings || typeof settings !== 'object') return settings;
  const next = { ...settings };
  next.backgroundImage = normalizeBackgroundPersistenceValue(next.backgroundImage || '');
  if (typeof next.image === 'string' && /^blob:/i.test(next.image)) {
    next.image = '';
  }
  return next;
};

export const sanitizeForExport = (payload = {}, issues = []) => {
  const cloned = deepClone(payload);
  const recordIssue = (msg) => issues && issues.push(msg);

  const ensureDividerContent = (content) => {
    const normalized = normalizeDividerContent(content, issues);
    if (content && typeof content !== 'object') {
      recordIssue?.('Divider content coerced from non-object during export; defaulted.');
    }
    return {
      ...DEFAULT_DIVIDER_CONTENT,
      ...normalized,
      isSlideBreak: normalized.isSlideBreak === true
    };
  };

  const sanitizeBlocks = (blocks) =>
    (Array.isArray(blocks) ? blocks : []).map((block) => {
      if (!block || typeof block !== 'object') return block;

      if (block.type === 'divider') {
        const sourceContent = block?.data?.content !== undefined ? block.data.content : block.content;
        const normalizedContent = ensureDividerContent(sourceContent);
        const hasSlideBreakFlag = block?.data?.isSlideBreak === true;
        const contentWithSlideBreak = hasSlideBreakFlag
          ? { ...normalizedContent, isSlideBreak: true }
          : normalizedContent;
        const nextData = block.data && typeof block.data === 'object' && !Array.isArray(block.data)
          ? { ...block.data, content: contentWithSlideBreak, isSlideBreak: contentWithSlideBreak.isSlideBreak }
          : { content: contentWithSlideBreak, isSlideBreak: contentWithSlideBreak.isSlideBreak };

        return {
          ...block,
          content: contentWithSlideBreak,
          data: nextData
        };
      }

      if (block.data && typeof block.data === 'object') {
        return { ...block, data: { ...block.data } };
      }

      return block;
    });

  const sanitizeSectionValue = (value) => {
    if (Array.isArray(value)) return sanitizeBlocks(value);
    if (value && typeof value === 'object') {
      return {
        ...value,
        text: sanitizeText(value.text),
        blocks: sanitizeBlocks(value.blocks)
      };
    }
    return value;
  };

  if (Array.isArray(cloned.sections)) {
    cloned.sections = cloned.sections.map((section) => ({
      ...section,
      text: sanitizeText(section?.text),
      ...(section?.sectionTitleCard ? { sectionTitleCard: normalizeCardBackgroundSettingsForExport(section.sectionTitleCard) } : {}),
      blocks: sanitizeBlocks(section?.blocks)
    }));
  } else if (cloned.sections && typeof cloned.sections === 'object') {
    cloned.sections = Object.entries(cloned.sections).reduce((acc, [key, value]) => {
      const normalizedSection = sanitizeSectionValue(value);
      acc[key] = {
        ...normalizedSection,
        ...(normalizedSection?.sectionTitleCard ? { sectionTitleCard: normalizeCardBackgroundSettingsForExport(normalizedSection.sectionTitleCard) } : {})
      };
      return acc;
    }, {});
  }

  if (Array.isArray(cloned.rawSections)) {
    cloned.rawSections = cloned.rawSections.map((section) => ({
      ...section,
      text: sanitizeText(section?.text),
      blocks: sanitizeBlocks(section?.blocks)
    }));
  }

  if (Array.isArray(cloned.blocks)) {
    cloned.blocks = sanitizeBlocks(cloned.blocks);
  }

  const contentBlocks = cloned?.extensions?.boppps_planner?.content_blocks;
  if (Array.isArray(contentBlocks)) {
    cloned.extensions.boppps_planner.content_blocks = contentBlocks.map((section) => ({
      ...section,
      text: sanitizeText(section?.text),
      ...(section?.sectionTitleCard ? { sectionTitleCard: normalizeCardBackgroundSettingsForExport(section.sectionTitleCard) } : {}),
      blocks: sanitizeBlocks(section?.blocks)
    }));
  }

  cloned.bodyBackground = normalizeBackgroundPersistenceValue(cloned.bodyBackground || 'white') || 'white';
  cloned.slideBackgrounds = normalizeBackgroundMapForPersistence(cloned.slideBackgrounds);
  cloned.titleCardBackground = normalizeBackgroundPersistenceValue(cloned.titleCardBackground, { allowEmpty: false });
  if (cloned.titleCardSettings && typeof cloned.titleCardSettings === 'object') {
    cloned.titleCardSettings = normalizeCardBackgroundSettingsForExport(cloned.titleCardSettings);
  }
  if (cloned.endTitleCardSettings && typeof cloned.endTitleCardSettings === 'object') {
    cloned.endTitleCardSettings = normalizeCardBackgroundSettingsForExport(cloned.endTitleCardSettings);
  }

  const extensionUiState = cloned?.extensions?.boppps_planner?.ui_state;
  if (extensionUiState && typeof extensionUiState === 'object') {
    extensionUiState.bodyBackground = normalizeBackgroundPersistenceValue(extensionUiState.bodyBackground || 'white') || 'white';
    extensionUiState.slideBackgrounds = normalizeBackgroundMapForPersistence(extensionUiState.slideBackgrounds);
    extensionUiState.titleCardBackground = normalizeBackgroundPersistenceValue(extensionUiState.titleCardBackground, { allowEmpty: false });
    if (extensionUiState.titleCardSettings && typeof extensionUiState.titleCardSettings === 'object') {
      extensionUiState.titleCardSettings = normalizeCardBackgroundSettingsForExport(extensionUiState.titleCardSettings);
    }
    if (extensionUiState.endTitleCardSettings && typeof extensionUiState.endTitleCardSettings === 'object') {
      extensionUiState.endTitleCardSettings = normalizeCardBackgroundSettingsForExport(extensionUiState.endTitleCardSettings);
    }
  }

  return cloned;
};

export const prepareJsonForDownload = (payload = {}, { stripFn } = {}) => {
  // First pass to recover divider objects.
  const sanitized = sanitizeForExport(payload);
  const maybeStripped = stripFn ? stripFn(sanitized) : sanitized;
  // Last-mile guard in case the strip step reintroduces stringified content.
  return sanitizeForExport(maybeStripped);
};

const normalizeContentBlock = (block = {}, issues, generateIdFn) => {
  const id = safeString(block.id || block.blockId || block.key || block.slug, generateIdFn());
  const normalized = { ...block, id };

  normalized.type = safeString(block.type || block.blockType || 'text');

  const stringFields = [
    'title',
    'blockTitle',
    'heading',
    'caption',
    'alt',
    'size',
    'layout',
    'style',
    'imageTitle',
    'imageAuthor',
    'imageSource',
    'imageDate',
    'imageMedium',
    'imageReferenceCategory',
    'imageReferenceTitle',
    'imageArticleTitle',
    'description',
    'slug',
    'sectionTitle',
    'sectionId',
    'videoUrl',
    'audioUrl',
    'html',
    'prompt',
    'label',
    'name'
  ];

  stringFields.forEach((field) => {
    if (field in normalized) {
      normalized[field] = safeString(normalized[field]);
    }
  });

  const incomingContent = block.content ?? block.data?.content;
  if (normalized.type === 'divider') {
    normalized.content = normalizeDividerContent(incomingContent, issues);
  } else if (typeof incomingContent === 'string') {
    normalized.content = safeString(incomingContent, '');
  } else if (incomingContent === null || incomingContent === undefined) {
    normalized.content = '';
  } else {
    normalized.content = safeString(incomingContent, '');
  }

  normalized.items = ensureArray(block.items);
  normalized.cardItems = ensureArray(block.cardItems);
  normalized.stackedCardItems = ensureArray(block.stackedCardItems);
  normalized.accordionItems = ensureArray(block.accordionItems || block.items);
  normalized.questions = ensureArray(block.questions);

  if (!normalized.type) {
    issues.push(`Block ${id} missing type; defaulted to "text"`);
    normalized.type = 'text';
  }

  return normalized;
};

const toTitleCase = (value) => {
  const safe = safeString(value);
  if (!safe) return '';
  return safe
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Turn `slideId` groups into the slide breaks the editor actually splits on.
 *
 * A section becomes several slides where it carries `divider` blocks flagged
 * `content.isSlideBreak` — that is what the editor, the presenter and every
 * exporter read. A block's `slideId` only *names* the slide it lands on.
 *
 * A deck written by an assistant groups by `slideId` and emits no dividers,
 * because `slideId` is what the prompt's schema describes. Loaded as-is, every
 * block in a section piled onto one slide: nine activities stacked in the same
 * place, each headline drawn over the last. This bridges the two — one break
 * between each run of blocks that share a `slideId`.
 *
 * A section that already has breaks is left exactly as it is; so is one whose
 * blocks all belong to a single slide.
 */
export const insertSlideBreaksFromSlideIds = (blocks, generateIdFn = generateId) => {
  if (!Array.isArray(blocks) || blocks.length < 2) return blocks;

  const hasBreak = blocks.some((block) =>
    block && block.type === 'divider' && block.content && block.content.isSlideBreak === true);
  if (hasBreak) return blocks;

  const slideIdOf = (block) => (block && (block.slideId || (block.content && block.content.slideId))) || '';
  const distinct = new Set(blocks.map(slideIdOf).filter(Boolean));
  // Nothing to split on: no slide ids at all, or every block on one slide.
  if (distinct.size < 2) return blocks;

  const result = [];
  let current = null;
  blocks.forEach((block) => {
    const slideId = slideIdOf(block);
    // A block with no slide id continues the slide it follows rather than
    // starting one — an assistant that forgot the field on one block should
    // not gain an empty slide from it.
    if (slideId && current !== null && slideId !== current) {
      result.push({
        id: generateIdFn(),
        type: 'divider',
        slideId,
        content: { ...DEFAULT_DIVIDER_CONTENT, isSlideBreak: true, slideId }
      });
    }
    if (slideId) current = slideId;
    result.push(block);
  });

  return result;
};

export const normalizeLectureJson = (raw, { generateIdFn = generateId } = {}) => {
  const issues = [];
  if (!raw || typeof raw !== 'object') {
    return { success: false, normalized: null, issues: ['Imported file is not a valid JSON object.'] };
  }

  const normalized = {
    courseTopic: safeString(raw.courseTopic, 'Course Topic - Week 1'),
    instructorName: safeString(raw.instructorName, 'Instructor: Your Name'),
    instructorEmail: safeString(raw.instructorEmail, 'email@institution.edu'),
    footerCourseInfo: safeString(raw.footerCourseInfo, 'Course Title - Course Code'),
    footerInstitution: safeString(raw.footerInstitution, 'Institution Name'),
    footerCopyright: safeString(raw.footerCopyright, '© 2025 All Rights Reserved'),
    showHeaderBar: raw.showHeaderBar !== false,
    week: safeString(raw.week, '1'),
    date: safeString(raw.date, new Date().toISOString().split('T')[0]),
    bodyLayout: safeString(raw.bodyLayout, 'slides'),
    slideDesignMode: raw.slideDesignMode === 'canvas' ? 'canvas' : 'flow',
    bodyBackground: normalizeBackgroundPersistenceValue(raw.bodyBackground || 'white') || 'white',
    slideBackgrounds: normalizeBackgroundMapForPersistence(raw.slideBackgrounds),
    titleCardBackground: normalizeBackgroundPersistenceValue(raw.titleCardBackground, { allowEmpty: false }),
    slideHeaderMode: safeString(raw.slideHeaderMode, 'single'),
    slideHeaderColor: safeString(raw.slideHeaderColor, '#7c3aed'),
    slideHeaderColors: raw.slideHeaderColors || {},
    slideGlobalCss: safeString(raw.slideGlobalCss, ''),
    titleCardSettings: normalizeTitleCardBackgroundSettings(raw.titleCardSettings || {}),
    endTitleCardSettings: normalizeTitleCardBackgroundSettings(raw.endTitleCardSettings || {}),
    slideVisibility: raw.slideVisibility || {},
    slideNames: raw.slideNames || {},
    slideSubtitles: raw.slideSubtitles || {},
    slideDurations: raw.slideDurations || {},
    slideDeckName: safeString(raw.slideDeckName || raw.exportTitle, 'Slide Deck'),
    exportFileName: safeString(raw.exportFileName || raw.exportTitle, ''),
    htmlExportTitle: safeString(raw.htmlExportTitle || raw.exportTitle, ''),
    exportVersion: raw.exportVersion || 1,
    schoolLogo: raw.schoolLogo,
    schoolLogoName: safeString(raw.schoolLogoName, ''),
    schoolLogoSize: raw.schoolLogoSize || 0,
    showAspectGuides: raw.showAspectGuides === true
  };

  const rawSections = (() => {
    if (Array.isArray(raw.sections)) return raw.sections;

    if (raw.sections && typeof raw.sections === 'object') {
      issues.push('Converted legacy sections object to array.');
      return Object.entries(raw.sections).map(([id, value]) => {
        if (Array.isArray(value)) {
          return { id, blocks: value };
        }
        if (value && typeof value === 'object') {
          return { id, ...value };
        }
        issues.push(`Section ${id} had unexpected shape; treated as empty.`);
        return { id, blocks: [] };
      });
    }
    if (Array.isArray(raw.slides)) {
      issues.push('Converted legacy slides array to sections.');
      return [
        {
          id: 'imported',
          title: 'Imported Content',
          type: 'content',
          blocks: raw.slides.flatMap((slide) => slide.blocks || slide.contentBlocks || [])
        }
      ];
    }
    issues.push('Missing sections; defaulted to empty array.');
    return [];
  })();

  normalized.sections = rawSections.map((section) => {
    const sectionId = safeString(section.id || section.sectionId || section.name, generateIdFn());
    if (!section.id) issues.push(`Section missing id; assigned ${sectionId}`);
    const title = safeString(section.title || section.name, toTitleCase(sectionId));

    const legacyBlocks = Array.isArray(section) ? section : undefined;
    const rawBlocks = ensureArray(section.blocks || section.contentBlocks || legacyBlocks);
    const blocks = insertSlideBreaksFromSlideIds(rawBlocks, generateIdFn);
    if (blocks.length !== rawBlocks.length) {
      issues.push(`Section ${section.id || 'unknown'}: added ${blocks.length - rawBlocks.length} slide break(s) from slideId groups.`);
    }

    const sectionTitleCard = normalizeTitleCardBackgroundSettings(section.sectionTitleCard);

    return {
      ...section,
      id: sectionId,
      title,
      type: safeString(section.type || section.sectionType || 'content'),
      sectionTitleCard,
      blocks: blocks.map((block) => normalizeContentBlock(block, issues, generateIdFn))
    };
  });

  return { success: true, normalized, issues };
};

/**
 * The same repair for a list of sections, for paths that set sections straight
 * into state — autosave recovery, which restores whatever was stored, including
 * a deck that was collapsed before this repair existed.
 */
export const ensureSlideBreaksInSections = (sections = [], { generateIdFn = generateId } = {}) =>
  (Array.isArray(sections) ? sections : []).map((section) => {
    if (!section || !Array.isArray(section.blocks)) return section;
    const blocks = insertSlideBreaksFromSlideIds(section.blocks, generateIdFn);
    return blocks === section.blocks ? section : { ...section, blocks };
  });

export const normalizeSectionsDividerContent = (sections = [], issues) =>
  (Array.isArray(sections) ? sections : []).map((section) => ({
    ...section,
    blocks: ensureArray(section?.blocks).map((block) => {
      if (!block || block.type !== 'divider') return block;
      const next = { ...block };
      next.content = normalizeDividerContent(block.content, issues);
      return next;
    })
  }));
